import { useCallback, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  IconCashBanknoteMove,
  IconDotsVertical,
  IconFileEuro,
  IconFileText,
  IconLock,
  IconPlus,
  IconUser,
} from "@tabler/icons-react";
import moment from "moment/moment";
import { Table } from "../UI/Table";
import Status from "../UI/Status";
import Callout from "../UI/Callout";
// @ts-expect-error pure js
import IsLoading from "../UI/IsLoading";
import EmptyState from "../UI/EmptyState";
import Button from "../UI/Button";
import Text from "../UI/Text";
import DropdownMenu from "../UI/DropdownMenu";
import ModalDialog, {
  ModalDialogBody,
  ModalDialogFooter,
} from "../UI/ModalDialog";
import { Selectable } from "../UI/Selectable";
import FilterBar from "../UI/FilterBar/FilterBar";
import FilterSection from "../UI/FilterBar/FilterSection";
import { useToast } from "../UI/Toast/useToast";
// @ts-expect-error pure js
import TextInputStandalone from "../UI/TextInputStandalone";
// @ts-expect-error pure js
import { ReactSelectStandalone } from "../UI/SelectStandalone";
// @ts-expect-error pure js
import useProfileBailleurs from "../../hooks/use-profile-bailleurs";
// @ts-expect-error pure js
import { handleAPIError } from "../../utils";
// @ts-expect-error pure js
import { currencyFormatter, dateConvert } from "../../api/Functions";
import MoisPicker from "./MoisPicker";
import {
  useBalanceComptable,
  useMandatsGestion,
} from "../../hooks/use-compta-pro";
import type { MandatGestion } from "../../models/comptabilite";
import type { ProfileBailleur } from "../Profile/types";
import {
  numeroMandant,
  periodeCourante,
  periodeLabel,
  type Periode,
} from "./comptaUtils";
import FichierVirementModal, {
  type VirementLigne,
} from "./FichierVirementModal";
import styles from "./Compta.module.css";

interface ProfileOption {
  value: number;
  label: string;
}

const STATUT_OPTIONS = [
  { value: "actif", label: "Actif" },
  { value: "termine", label: "Terminé" },
];

const SOLDE_OPTIONS = [
  { value: "positif", label: "Solde à reverser" },
  { value: "nul", label: "Solde nul ou débiteur" },
];

function numeroAffiche(mandat: MandatGestion): string {
  return mandat.mandate_number || String(mandat.numero);
}

function estActif(mandat: MandatGestion): boolean {
  return (
    mandat.date_fin === null ||
    moment(mandat.date_fin).isSameOrAfter(moment(), "day")
  );
}

export default function MandatsTab() {
  const toast = useToast();
  const navigate = useNavigate();
  const {
    mandats,
    isLoading,
    creerMandat,
    cloreMandat,
    genererFacturesHonoraires,
    mutate,
  } = useMandatsGestion();
  const { profileBailleurs } = useProfileBailleurs() as {
    profileBailleurs: ProfileBailleur[];
  };
  const { data: balanceMandants } = useBalanceComptable(
    periodeCourante().year,
    periodeCourante().month,
    "mandant",
  );

  const soldes = useMemo(() => {
    const byNumero: Record<string, number> = {};
    (balanceMandants?.rows ?? []).forEach((row) => {
      byNumero[row.numero] = parseFloat(row.solde);
    });
    return byNumero;
  }, [balanceMandants]);

  const [createOpen, setCreateOpen] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<ProfileOption | null>(
    null,
  );
  const [dateDebut, setDateDebut] = useState(moment().format("YYYY-MM-DD"));
  const [mandateNumber, setMandateNumber] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [clotureMandat, setClotureMandat] = useState<MandatGestion | null>(
    null,
  );
  const [dateFin, setDateFin] = useState(moment().format("YYYY-MM-DD"));

  const [facturePayload, setFacturePayload] = useState<{
    mandatIds: number[];
    resetSelected?: () => void;
  } | null>(null);
  const [facturePeriode, setFacturePeriode] = useState<Periode>(
    periodeCourante(),
  );

  const [virementPayload, setVirementPayload] = useState<{
    mandatIds: number[];
    resetSelected: () => void;
  } | null>(null);

  const [search, setSearch] = useState("");
  const [filterStatut, setFilterStatut] = useState<string[]>([]);
  const [filterSolde, setFilterSolde] = useState<string[]>([]);

  const soldeMandat = useCallback(
    (mandat: MandatGestion) =>
      soldes[numeroMandant(mandat.id_profile_bailleur)] ?? 0,
    [soldes],
  );

  const mandatsFiltres = useMemo(() => {
    const searchLower = search.toLowerCase();
    return mandats.filter((mandat) => {
      if (
        searchLower &&
        !mandat.nom_profile?.toLowerCase().includes(searchLower) &&
        !numeroAffiche(mandat).toLowerCase().includes(searchLower)
      ) {
        return false;
      }
      if (
        filterStatut.length > 0 &&
        !filterStatut.includes(estActif(mandat) ? "actif" : "termine")
      ) {
        return false;
      }
      if (filterSolde.length > 0) {
        const positif = soldeMandat(mandat) > 0;
        if (!filterSolde.includes(positif ? "positif" : "nul")) {
          return false;
        }
      }
      return true;
    });
  }, [mandats, search, filterStatut, filterSolde, soldeMandat]);

  const factureMandats = (facturePayload?.mandatIds ?? [])
    .map((id) => mandats.find((mandat) => mandat.id === id))
    .filter((mandat): mandat is MandatGestion => Boolean(mandat));

  const virementLignes: VirementLigne[] = (virementPayload?.mandatIds ?? [])
    .map((id) => mandats.find((mandat) => mandat.id === id))
    .filter((mandat): mandat is MandatGestion => Boolean(mandat))
    .map((mandat) => {
      const profile = profileBailleurs.find(
        (candidat) => candidat.id === mandat.id_profile_bailleur,
      );
      return {
        idProfileBailleur: mandat.id_profile_bailleur,
        nom: mandat.nom_profile ?? "",
        solde: soldeMandat(mandat),
        ibanManquant: !profile?.iban || !profile?.bic,
      };
    });

  const profileOptions: ProfileOption[] = profileBailleurs.map((profile) => ({
    value: profile.id,
    label: profile.nom_profile,
  }));

  async function submitCreate() {
    if (!selectedProfile) return;
    setIsSubmitting(true);
    try {
      await creerMandat({
        id_profile_bailleur: selectedProfile.value,
        date_debut: dateDebut,
        mandate_number: mandateNumber.trim() || null,
      });
      toast.success({ title: "Mandat créé" });
      setCreateOpen(false);
      setSelectedProfile(null);
      setMandateNumber("");
    } catch (error) {
      toast.error({
        title: "Création impossible",
        description: handleAPIError(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submitCloture() {
    if (!clotureMandat) return;
    setIsSubmitting(true);
    try {
      await cloreMandat(clotureMandat.id, dateFin);
      toast.success({ title: "Mandat clos" });
      setClotureMandat(null);
      await mutate();
    } catch (error) {
      toast.error({
        title: "Clôture impossible",
        description: handleAPIError(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  async function submitFacture() {
    if (!facturePayload) return;
    setIsSubmitting(true);
    try {
      const summary = await genererFacturesHonoraires(
        factureMandats.map((mandat) => mandat.id_profile_bailleur),
        facturePeriode.year,
        facturePeriode.month,
      );
      toast.success({
        title: `${summary.factures.length} facture(s) d'honoraires générée(s)`,
        description:
          summary.skipped.length > 0
            ? `Disponibles dans les documents des profils. Ignorés : ${summary.skipped.join(
                " ; ",
              )}`
            : "Disponibles dans les documents de chaque profil.",
      });
      facturePayload.resetSelected?.();
      setFacturePayload(null);
    } catch (error) {
      toast.error({
        title: "Génération impossible",
        description: handleAPIError(error),
      });
    } finally {
      setIsSubmitting(false);
    }
  }

  const activeFiltersCount =
    (filterStatut.length > 0 ? 1 : 0) + (filterSolde.length > 0 ? 1 : 0);

  return (
    <div className={styles.stack}>
      <ModalDialog
        open={createOpen}
        onOpenChange={setCreateOpen}
        header="Nouveau mandat de gestion"
        size="sm"
      >
        <div className={styles.modalForm}>
          <Text>
            Le mandant entre dans la comptabilité mandants à la date de début :
            ses encaissements, honoraires et reversements sont projetés au
            journal, et son solde antérieur est repris en à-nouveaux.
          </Text>
          <ReactSelectStandalone
            label="Profil bailleur mandant"
            options={profileOptions}
            value={selectedProfile}
            onChange={(option: ProfileOption | null) =>
              setSelectedProfile(option)
            }
            placeholder="Choisir un profil"
            menuPortalTarget={document.body}
          />
          <TextInputStandalone
            label="Numéro de mandat"
            placeholder="Tel qu'il figure sur le contrat"
            maxLength={50}
            value={mandateNumber}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setMandateNumber(e.target.value)
            }
          />
          <TextInputStandalone
            label="Début du mandat"
            type="date"
            value={dateDebut}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDateDebut(e.target.value)
            }
          />
        </div>
        <ModalDialogFooter>
          <Button variant="secondary" onClick={() => setCreateOpen(false)}>
            Annuler
          </Button>
          <Button
            onClick={submitCreate}
            disabled={!selectedProfile || !dateDebut}
            isLoading={isSubmitting}
          >
            Créer le mandat
          </Button>
        </ModalDialogFooter>
      </ModalDialog>

      <ModalDialog
        open={clotureMandat !== null}
        onOpenChange={(open) => {
          if (!open) setClotureMandat(null);
        }}
        header={`Clore le mandat n°${
          clotureMandat ? numeroAffiche(clotureMandat) : ""
        }`}
        size="sm"
      >
        <div className={styles.modalForm}>
          <Text>
            Après la date de fin, les opérations de ce mandant ne sont plus
            projetées au journal.
          </Text>
          <TextInputStandalone
            label="Date de fin"
            type="date"
            value={dateFin}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setDateFin(e.target.value)
            }
          />
        </div>
        <ModalDialogFooter>
          <Button variant="secondary" onClick={() => setClotureMandat(null)}>
            Annuler
          </Button>
          <Button onClick={submitCloture} isLoading={isSubmitting}>
            Clore le mandat
          </Button>
        </ModalDialogFooter>
      </ModalDialog>

      <ModalDialog
        open={facturePayload !== null}
        onOpenChange={(open) => {
          if (!open) setFacturePayload(null);
        }}
        header={
          factureMandats.length > 1
            ? `Factures d'honoraires — ${factureMandats.length} mandants`
            : `Facture d'honoraires — ${factureMandats[0]?.nom_profile ?? ""}`
        }
        size="sm"
      >
        <ModalDialogBody className={styles.modalForm}>
          <Text>
            Génère la facture des honoraires de gestion prélevés sur le mois
            choisi ({periodeLabel(facturePeriode)}) pour chaque mandant
            sélectionné, avec le détail HT / TVA / TTC par bien. Les factures
            sont rangées dans les documents de chaque profil.
          </Text>
          {factureMandats.length > 1 && (
            <Text variant="deci">
              {factureMandats.map((mandat) => mandat.nom_profile).join(", ")}
            </Text>
          )}
          <MoisPicker
            label="Mois facturé"
            value={facturePeriode}
            onChange={setFacturePeriode}
          />
        </ModalDialogBody>
        <ModalDialogFooter>
          <Button variant="secondary" onClick={() => setFacturePayload(null)}>
            Annuler
          </Button>
          <Button
            onClick={submitFacture}
            disabled={factureMandats.length === 0}
            isLoading={isSubmitting}
          >
            {factureMandats.length > 1
              ? `Générer ${factureMandats.length} factures`
              : "Générer la facture"}
          </Button>
        </ModalDialogFooter>
      </ModalDialog>

      <FichierVirementModal
        open={virementPayload !== null}
        onOpenChange={(open) => {
          if (!open) setVirementPayload(null);
        }}
        lignes={virementLignes}
        onGenerated={() => virementPayload?.resetSelected()}
      />

      <div className={styles.toolbar}>
        <Callout variant="info">
          Le registre des mandats définit le périmètre de la comptabilité :
          seuls les profils sous mandat actif sont projetés au journal.
        </Callout>
        <div className={styles.toolbarSpacer}>
          <Button icon={IconPlus} onClick={() => setCreateOpen(true)}>
            Nouveau mandat
          </Button>
        </div>
      </div>
      {isLoading ? (
        <IsLoading />
      ) : mandats.length === 0 ? (
        <EmptyState
          icon={IconFileText}
          title="Aucun mandat de gestion"
          description="Créez un mandat pour chaque profil bailleur que vous gérez : c'est lui qui active la comptabilité mandants."
        />
      ) : (
        <Selectable items={mandatsFiltres}>
          <FilterBar
            searchValue={search}
            onSearchChange={setSearch}
            searchPlaceholder="Rechercher un mandant ou un numéro de mandat"
            activeFiltersCount={activeFiltersCount}
            onClearFilters={() => {
              setFilterStatut([]);
              setFilterSolde([]);
            }}
            leftActions={
              <>
                <Selectable.ActionButton
                  icon={IconFileEuro}
                  onClick={(
                    selectedItems: (number | string)[],
                    resetSelected: () => void,
                  ) => {
                    setFacturePeriode(periodeCourante());
                    setFacturePayload({
                      mandatIds: selectedItems as number[],
                      resetSelected,
                    });
                  }}
                >
                  Factures d'honoraires
                </Selectable.ActionButton>
                <Selectable.ActionButton
                  variant="secondary"
                  icon={IconCashBanknoteMove}
                  onClick={(
                    selectedItems: (number | string)[],
                    resetSelected: () => void,
                  ) => {
                    setVirementPayload({
                      mandatIds: selectedItems as number[],
                      resetSelected,
                    });
                  }}
                >
                  Fichier de virement
                </Selectable.ActionButton>
              </>
            }
          >
            <FilterSection
              title="Statut du mandat"
              options={STATUT_OPTIONS}
              values={filterStatut}
              onChange={setFilterStatut}
              defaultOpen
            />
            <FilterSection
              title="Solde"
              options={SOLDE_OPTIONS}
              values={filterSolde}
              onChange={setFilterSolde}
            />
          </FilterBar>
          <Table variant="card">
            <Table.Header>
              <Table.Column>
                <Selectable.Checkbox selectAll />
              </Table.Column>
              <Table.Column>N°</Table.Column>
              <Table.Column>Mandant</Table.Column>
              <Table.Column>Début</Table.Column>
              <Table.Column>Fin</Table.Column>
              <Table.Column>Statut</Table.Column>
              <Table.Column align="right">Solde dû</Table.Column>
              <Table.Column align="right">Actions</Table.Column>
            </Table.Header>
            <Table.Body>
              {mandatsFiltres.map((mandat, index) => (
                <Table.Row
                  key={mandat.id}
                  className={styles.clickableRow}
                  onClick={() =>
                    navigate(`/bailleur/profiles/${mandat.id_profile_bailleur}`)
                  }
                >
                  <Table.Cell onClick={(e) => e.stopPropagation()}>
                    <Selectable.Checkbox value={mandat.id} index={index} />
                  </Table.Cell>
                  <Table.Cell>{numeroAffiche(mandat)}</Table.Cell>
                  <Table.Cell>{mandat.nom_profile}</Table.Cell>
                  <Table.Cell>{dateConvert(mandat.date_debut)}</Table.Cell>
                  <Table.Cell>
                    {mandat.date_fin ? dateConvert(mandat.date_fin) : "—"}
                  </Table.Cell>
                  <Table.Cell>
                    <Status
                      color={estActif(mandat) ? "success" : "neutral"}
                      text={estActif(mandat) ? "Actif" : "Terminé"}
                    />
                  </Table.Cell>
                  <Table.Cell align="right">
                    <Text
                      as="span"
                      weight="bold"
                      color={soldeMandat(mandat) > 0 ? "success" : "neutral"}
                    >
                      {currencyFormatter(soldeMandat(mandat), {
                        withSign: true,
                      })}
                    </Text>
                  </Table.Cell>
                  <Table.Cell align="right">
                    <DropdownMenu
                      icon={IconDotsVertical}
                      label="Actions du mandat"
                      align="end"
                      className={styles.action}
                    >
                      <DropdownMenu.Item
                        icon={IconUser}
                        label="Voir le profil"
                        onClick={() =>
                          navigate(
                            `/bailleur/profiles/${mandat.id_profile_bailleur}`,
                          )
                        }
                      />
                      <DropdownMenu.Item
                        icon={IconFileEuro}
                        label="Facture d'honoraires"
                        onClick={() =>
                          setFacturePayload({ mandatIds: [mandat.id] })
                        }
                      />
                      {estActif(mandat) && (
                        <DropdownMenu.Item
                          icon={IconLock}
                          variant="danger"
                          label="Clore le mandat"
                          onClick={() => {
                            setDateFin(moment().format("YYYY-MM-DD"));
                            setClotureMandat(mandat);
                          }}
                        />
                      )}
                    </DropdownMenu>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </Selectable>
      )}
    </div>
  );
}
